import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Scale, GraduationCap, Globe, Award, ArrowRight, Users, BookMarked, Building2, CheckCircle, MessageCircle, FileText, PenTool, Shield, Star } from "lucide-react";

export default function Home() {
  const whatsappLink = "https://wa.me/916284607500";

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center bg-slate-950">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1920')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/95 to-slate-950/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-[1px] w-12 bg-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">
                Excellence in Academic Publishing
              </span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium text-white leading-tight mb-6">
              SPA Publication <span className="text-[#C5A059]">Group</span>
            </h1>
            <p className="text-slate-400 text-lg sm:text-xl leading-relaxed mb-10 max-w-xl">
              A premier academic publishing consortium dedicated to promoting scholarly
              excellence across national and international platforms.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild data-testid="hero-publish-btn" className="bg-[#C5A059] hover:bg-[#a88a48] text-slate-950 rounded-none px-8 py-6 text-sm font-medium tracking-widest uppercase">
                <Link to="/submission">Publish With Us <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="outline" data-testid="hero-publications-btn" className="border-slate-600 text-white hover:bg-white/10 rounded-none px-8 py-6 text-sm font-medium tracking-widest uppercase">
                <Link to="/publications">View Publications</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="absolute bottom-0 left-0 right-0 bg-slate-900/80 backdrop-blur-sm border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <BookMarked className="w-6 h-6 text-[#C5A059] mx-auto mb-2" />
                <p className="font-serif text-2xl sm:text-3xl font-medium text-white">500+</p>
                <p className="text-slate-400 text-sm">Publications</p>
              </div>
              <div>
                <Users className="w-6 h-6 text-[#C5A059] mx-auto mb-2" />
                <p className="font-serif text-2xl sm:text-3xl font-medium text-white">1000+</p>
                <p className="text-slate-400 text-sm">Authors</p>
              </div>
              <div>
                <Building2 className="w-6 h-6 text-[#C5A059] mx-auto mb-2" />
                <p className="font-serif text-2xl sm:text-3xl font-medium text-white">5</p>
                <p className="text-slate-400 text-sm">Companies</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">About Us</span>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium text-slate-900 mt-3 mb-6">
                Advancing Knowledge Through Publishing
              </h2>
              <div className="w-16 h-[3px] bg-[#C5A059] mb-8" />
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                SPA Publication Group is a distinguished academic publishing consortium
                comprising five specialized publishing houses. We promote scholarly excellence
                by providing a platform for researchers, academicians, and professionals.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                We handle authored books, edited volumes, research monographs, and academic textbooks
                across all disciplines including legal studies, social sciences, humanities, and sciences.
              </p>
              <Button asChild data-testid="intro-learn-more-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-none px-8 py-6 text-sm font-medium tracking-widest uppercase">
                <Link to="/about">Learn More <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-slate-100 p-8 text-center">
                  <p className="font-serif text-4xl font-medium text-[#C5A059]">15+</p>
                  <p className="text-slate-600 text-sm mt-2">Years Experience</p>
                </div>
                <div className="bg-slate-950 p-8 text-center">
                  <p className="font-serif text-4xl font-medium text-[#C5A059]">50+</p>
                  <p className="text-slate-300 text-sm mt-2">Subject Areas</p>
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="bg-[#C5A059] p-8 text-center">
                  <p className="font-serif text-4xl font-medium text-slate-950">200+</p>
                  <p className="text-slate-800 text-sm mt-2">Institutions</p>
                </div>
                <div className="bg-slate-100 p-8 text-center">
                  <p className="font-serif text-4xl font-medium text-slate-900">10+</p>
                  <p className="text-slate-600 text-sm mt-2">Countries</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer - NEW SECTION */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Our Services</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium text-white mt-3 mb-6">What We Offer</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Comprehensive publishing and academic support services for researchers worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-900 p-8 border border-slate-800 hover:border-[#C5A059]/50 transition-colors group">
              <BookOpen className="w-12 h-12 text-[#C5A059] mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="font-serif text-xl font-medium text-white mb-3">Book Publishing</h3>
              <p className="text-slate-400 text-sm mb-4">Authored books, edited volumes, and research monographs with ISBN</p>
              <Link to="/services" className="text-[#C5A059] text-sm font-medium hover:underline inline-flex items-center gap-1">
                Learn More <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="bg-slate-900 p-8 border border-slate-800 hover:border-[#C5A059]/50 transition-colors group">
              <FileText className="w-12 h-12 text-[#C5A059] mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="font-serif text-xl font-medium text-white mb-3">Content Writing</h3>
              <p className="text-slate-400 text-sm mb-4">Research papers, dissertations, thesis, and academic content</p>
              <Link to="/content-writing-services" className="text-[#C5A059] text-sm font-medium hover:underline inline-flex items-center gap-1">
                Learn More <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="bg-slate-900 p-8 border border-slate-800 hover:border-[#C5A059]/50 transition-colors group">
              <PenTool className="w-12 h-12 text-[#C5A059] mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="font-serif text-xl font-medium text-white mb-3">Pre-written Content</h3>
              <p className="text-slate-400 text-sm mb-4">Ready-to-publish books and chapters across all subjects</p>
              <Link to="/pre-written-content" className="text-[#C5A059] text-sm font-medium hover:underline inline-flex items-center gap-1">
                View Repository <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="bg-slate-900 p-8 border border-slate-800 hover:border-[#C5A059]/50 transition-colors group">
              <Shield className="w-12 h-12 text-[#C5A059] mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="font-serif text-xl font-medium text-white mb-3">AI & Plag Services</h3>
              <p className="text-slate-400 text-sm mb-4">Turnitin, Drillbit checks and plagiarism removal services</p>
              <Link to="/content-writing-services" className="text-[#C5A059] text-sm font-medium hover:underline inline-flex items-center gap-1">
                Learn More <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SCOPUS/WOS Highlight - NEW SECTION */}
      <section className="py-16 bg-gradient-to-r from-[#C5A059] via-[#d4b06a] to-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-8 h-8 text-slate-950" />
                <Star className="w-8 h-8 text-slate-950" />
                <Star className="w-8 h-8 text-slate-950" />
              </div>
              <div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-slate-950">SCOPUS | WOS | ABDC Publications</h3>
                <p className="text-slate-800">Get your research published in prestigious indexed journals</p>
              </div>
            </div>
            <Link to="/content-writing-services" className="bg-slate-950 hover:bg-slate-800 text-white px-8 py-4 font-medium flex items-center gap-2 transition-colors">
              Explore Now <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Our Publishing Houses</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium text-slate-900 mt-3 mb-6">Five Pillars of Excellence</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Each publishing house specializes in different types of academic publications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <CompanyCard 
              name="SPA Publications" 
              focus="Authored Books & Research" 
              scope="National" 
              slug="spa-publications" 
              logoUrl="https://customer-assets.emergentagent.com/job_37efc3c3-f1a5-4028-a803-e9ddc7d446f6/artifacts/7cl4e1on_IMG-20250617-WA0005.jpg"
            />
            <CompanyCard 
              name="Legal Luminaries" 
              focus="Edited Books All Subjects" 
              scope="National" 
              slug="legal-luminaries" 
              logoUrl="https://customer-assets.emergentagent.com/job_37efc3c3-f1a5-4028-a803-e9ddc7d446f6/artifacts/igfim3dc_1.png"
            />
            <CompanyCard 
              name="Intellect Jurists" 
              focus="Academic Textbooks" 
              scope="National" 
              slug="intellect-jurists" 
              logoUrl="https://customer-assets.emergentagent.com/job_37efc3c3-f1a5-4028-a803-e9ddc7d446f6/artifacts/mqtsm5l5_2.png"
            />
            <CompanyCard 
              name="Blue Globe International" 
              focus="International Publications" 
              scope="International" 
              slug="blue-globe-international"
              logoUrl="https://customer-assets.emergentagent.com/job_472d7425-aa73-46af-9a67-085a58da3658/artifacts/isjy6bwp_Blue%20GLobe.png"
            />
            <CompanyCard 
              name="Aquitas International" 
              focus="Global Academic Publications" 
              scope="International" 
              slug="aquitas-international"
              logoUrl="https://customer-assets.emergentagent.com/job_472d7425-aa73-46af-9a67-085a58da3658/artifacts/s1nj050z_Aquatics.png"
            />
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="outline" data-testid="view-all-companies-btn" className="border-slate-300 text-slate-700 hover:border-slate-900 rounded-none px-8 py-6 text-sm font-medium tracking-widest uppercase">
              <Link to="/companies">View All Companies <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us - NEW SECTION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da?crop=entropy&cs=srgb&fm=jpg&w=600" 
                  alt="Library"
                  className="w-full h-48 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1532012197267-da84d127e765?crop=entropy&cs=srgb&fm=jpg&w=600" 
                  alt="Books"
                  className="w-full h-48 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?crop=entropy&cs=srgb&fm=jpg&w=600" 
                  alt="Writing"
                  className="w-full h-48 object-cover col-span-2"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Why Choose Us</span>
              <h2 className="font-serif text-4xl font-medium text-slate-900 mt-3 mb-6">
                Trusted by Academics Worldwide
              </h2>
              <div className="w-16 h-[3px] bg-[#C5A059] mb-8" />
              <p className="text-slate-600 leading-relaxed mb-8">
                SPA Publication Group has established itself as a trusted name in academic publishing with a commitment to quality, integrity, and author satisfaction.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Rigorous peer review process ensuring quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">ISBN assignment for all publications</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">International reach through global divisions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Quick turnaround with transparent process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Affordable pricing for all authors</span>
                </li>
              </ul>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-4 font-medium transition-colors">
                <MessageCircle className="w-5 h-5" /> Contact on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial/Trust Section - NEW */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <Globe className="w-10 h-10 text-[#C5A059] mx-auto mb-4" />
              <h4 className="font-serif text-lg font-medium text-slate-900 mb-2">Global Reach</h4>
              <p className="text-slate-600 text-sm">Publishing across 10+ countries worldwide</p>
            </div>
            <div>
              <Shield className="w-10 h-10 text-[#C5A059] mx-auto mb-4" />
              <h4 className="font-serif text-lg font-medium text-slate-900 mb-2">Quality Assured</h4>
              <p className="text-slate-600 text-sm">Rigorous peer review and editing process</p>
            </div>
            <div>
              <Award className="w-10 h-10 text-[#C5A059] mx-auto mb-4" />
              <h4 className="font-serif text-lg font-medium text-slate-900 mb-2">ISBN Certified</h4>
              <p className="text-slate-600 text-sm">All publications receive official ISBN</p>
            </div>
            <div>
              <Users className="w-10 h-10 text-[#C5A059] mx-auto mb-4" />
              <h4 className="font-serif text-lg font-medium text-slate-900 mb-2">Expert Team</h4>
              <p className="text-slate-600 text-sm">Experienced editorial and review board</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Start Your Journey</span>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium text-white mt-3 mb-6">Ready to Publish Your Work?</h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Join thousands of researchers who have trusted us with their scholarly publications.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild data-testid="cta-submit-btn" className="bg-[#C5A059] hover:bg-[#a88a48] text-slate-950 rounded-none px-8 py-6 text-sm font-medium tracking-widest uppercase">
              <Link to="/submission">Submit Manuscript <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-sm font-medium tracking-widest uppercase inline-flex items-center gap-2 transition-colors">
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function CompanyCard({ icon: Icon, name, focus, scope, slug, color, logoUrl }) {
  return (
    <Link to={`/companies/${slug}`} data-testid={`company-card-${slug}`}>
      <Card className="h-full bg-white border border-slate-100 shadow-sm hover:shadow-lg hover:border-[#C5A059]/30 hover:-translate-y-1 transition-all duration-300 rounded-none overflow-hidden group">
        <CardContent className="p-8">
          {logoUrl ? (
            <div className="w-16 h-16 bg-slate-50 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform overflow-hidden">
              <img src={logoUrl} alt={name} className="w-full h-full object-contain p-1" />
            </div>
          ) : (
            <div className={`w-14 h-14 ${color || "bg-slate-100 text-slate-700"} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
              {Icon && <Icon className="w-7 h-7" />}
            </div>
          )}
          <span className={`inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase mb-4 ${scope === "International" ? "bg-[#C5A059]/10 text-[#C5A059]" : "bg-slate-100 text-slate-600"}`}>
            {scope}
          </span>
          <h3 className="font-serif text-xl font-medium text-slate-900 mb-3 group-hover:text-[#C5A059] transition-colors">{name}</h3>
          <p className="text-slate-500 text-sm">{focus}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
